import React, { useEffect } from 'react';
import './styles/home.scss';
import { serviceCategories } from './services';
import { valueCards } from './valueCards';
import { strings } from './strings';
import { useLocation } from 'react-router-dom';

export const HOME_ID = 'home';
export const ABOUT_ID = 'about-us';
export const SERVICES_ID = 'services';
export const VALUE_ID = 'value-delivered';
export const CONTACT_ID = 'contact-us';

// One icon per lifecycle category, rendered inside each service card's icon badge.
const SERVICE_ICONS: Record<(typeof serviceCategories)[number]['id'], React.ReactNode> = {
  digitize: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 8V5a1 1 0 0 1 1-1h3M20 8V5a1 1 0 0 0-1-1h-3M4 16v3a1 1 0 0 0 1 1h3M20 16v3a1 1 0 0 1-1 1h-3" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  ),
  'move-store': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-6 9 6-9 6-9-6z" />
      <path d="M3 9v6l9 6 9-6V9" />
    </svg>
  ),
  manage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  comply: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M9 13l2 2 4-4" />
    </svg>
  ),
  dispose: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  recover: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 8v4l3 2" />
    </svg>
  ),
};

const Home: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <div className="site-wrapper">
      <div className="content-container nav-section" id={HOME_ID}>
        <div className="home-container">
          <img className="home-bg-image" src={require('../assets/tmg_first_page.jpg')} alt="Futuristic network background" />
          <div className="home-text">
            <h1>{strings.home.title}</h1>
            <p>{strings.home.description1}</p>
            <p>{strings.home.description2}</p>
          </div>
        </div>

        <div className="services-container nav-section" id={SERVICES_ID}>
          <section>
            <h2 className="services-h2">{strings.services.title}</h2>

            <div className="services-blurb">
              <p>{strings.services.blurb}</p>
            </div>

            <div className="services-legend">
              <span>
                <i className="oem" />
                OEM
              </span>
              <span>
                <i className="tier1" />
                Tier 1&apos;s
              </span>
              <span>
                <i className="both" />
                Both
              </span>
            </div>

            <div className="services-panel">
              <div className="services-rule" />

              <div className="service-grid">
                {serviceCategories.map((category) => (
                  <div key={category.id} className="service-card">
                    <div className="service-card-icon">{SERVICE_ICONS[category.id]}</div>
                    <h3>{category.title}</h3>
                    <ul>
                      {category.items.map((item, index) => (
                        <li key={index} className={item.audience}>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="toolsight-strip">
                <p className="toolsight-name">
                  {strings.services.toolsight.name}
                  <span className="toolsight-sub">{strings.services.toolsight.tagline}</span>
                </p>

                <div className="toolsight-flow">
                  <div className="io inputs">{strings.services.toolsight.inputsLabel}</div>
                  <div className="spacer" />
                  <div className="arrow">
                    <svg viewBox="0 0 256 14" preserveAspectRatio="none">
                      <line x1="0" y1="7" x2="246" y2="7" stroke="rgba(255,255,255,0.55)" strokeWidth="2" />
                      <path d="M246 1 L256 7 L246 13 Z" fill="rgba(255,255,255,0.55)" />
                    </svg>
                  </div>
                  <div className="spacer" />
                  <div className="core">
                    {strings.services.toolsight.coreLabel}
                    <small>{strings.services.toolsight.coreSub}</small>
                  </div>
                  <div className="spacer" />
                  <div className="arrow">
                    <svg viewBox="0 0 256 14" preserveAspectRatio="none">
                      <line x1="0" y1="7" x2="246" y2="7" stroke="rgba(255,255,255,0.55)" strokeWidth="2" />
                      <path d="M246 1 L256 7 L246 13 Z" fill="rgba(255,255,255,0.55)" />
                    </svg>
                  </div>
                  <div className="spacer" />
                  <div className="io outputs">{strings.services.toolsight.outputsLabel}</div>
                </div>

                <p className="toolsight-note">{strings.services.toolsight.note}</p>
              </div>
            </div>
          </section>
        </div>

        <div className="value-container nav-section" id={VALUE_ID}>
          <section>
            <h2 className="value-h2">{strings.valueDelivered.title}</h2>
            <div className="value-grid">
              {valueCards.map((card, index) => (
                <div key={index} className="value-card">
                  <img src={card.image} alt="{card.title} background" className="value-card-bg" />
                  <div className="value-card-content">
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="about-container nav-section" id={ABOUT_ID}>
          <h2 className="about-h2">{strings.aboutUs.title}</h2>
          <div className="values-list">
            <div className="value-row">
              <h3 className="value-row-title">{strings.aboutUs.pillar1.title}</h3>
              <div className="value-row-box">
                <p>{strings.aboutUs.pillar1.text}</p>
              </div>
            </div>
            <div className="value-row">
              <h3 className="value-row-title">{strings.aboutUs.pillar2.title}</h3>
              <div className="value-row-box">
                <p>{strings.aboutUs.pillar2.text}</p>
              </div>
            </div>
            <div className="value-row">
              <h3 className="value-row-title">{strings.aboutUs.pillar3.title}</h3>
              <div className="value-row-box">
                <p>{strings.aboutUs.pillar3.text}</p>
              </div>
            </div>
          </div>
        </div>

        <footer className="footer">
          <div className="footer-contact nav-section" id={CONTACT_ID}>
            <h2 className="footer-contact-h2">Contact Us</h2>
            <p className="footer-contact-tagline">{strings.contact.tagline}</p>
            <div className="footer-contact-cards">
              <div className="footer-contact-card">
                <span className="footer-contact-icon">✉️</span>
                <p className="footer-contact-label">Email</p>
                <p className="footer-contact-name">{strings.contact.name}</p>
                <p className="footer-contact-role">{strings.contact.role}</p>
                <a href={strings.contact.emailLink} className="footer-contact-link">
                  Contact Us
                </a>
              </div>
              <div className="footer-contact-card">
                <img src={require('../assets/linkedIn.png')} alt="LinkedIn Logo" className="footer-contact-icon-img" />
                <p className="footer-contact-label">LinkedIn</p>

                <a
                  href="https://www.linkedin.com/company/tooling-management-group"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-contact-link"
                >
                  Join us on LinkedIn
                </a>
              </div>
              <div className="footer-contact-card">
                <span className="footer-contact-icon">📍</span>
                <p className="footer-contact-label">Location</p>
                <p className="footer-contact-address">{strings.contact.address}</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              {strings.footer.company} &nbsp;|&nbsp; {strings.footer.copyright}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home;
